/**
 * Нормалізація статус-колонок списків документів (рішення власника 2026-08-23):
 * у КОЖНІЙ таблиці порядок — бейдж міграції → проведено → вкладення → підпис →
 * решта; заголовки цих колонок без тексту (іконка), комірки — іконки
 * (StatusIcons). Застосовується автоматично всередині InfiniteDocGrid
 * (ag-Grid) і DataTable, тому сторінки лишають свої ColumnDef як є.
 *
 * Розпізнавання: посада колонки за field/key:
 *   posted → 'posted'; вкладення → colId/key '_att'; підпис → 'sign_control';
 *   бейдж міграції (ag-Grid) → headerName '' + width 44 (канон ImportedBadge),
 *   (DataTable) → importBadge: true (бейдж усередині колонки «№» — статус
 *   ставимо ОДРАЗУ ПІСЛЯ неї).
 */
import type { ReactNode } from 'react';
import type { ColDef } from 'ag-grid-community';
import type { ColumnDef } from '@/shared/components/DataTable';
import { AttachmentIcon, PostedIcon, SignIcon, AttachmentHeaderIcon, PostedHeaderIcon, SignHeaderIcon } from './StatusIcons';
import type { SignStatus } from '@/api/signControl';
import { unpostedNoteOf } from '@/shared/utils/unpostedNotes';

const ICON_W = 26;

// ---------------------------------------------------------------- ag-Grid
export function arrangeAgCols<T>(cols: ColDef<T>[]): ColDef<T>[] {
  const isBadge = (c: ColDef<T>) => c.headerName === '' && c.width === 44 && !c.field && !c.colId;
  const isPosted = (c: ColDef<T>) => c.field === 'posted' && !c.colId;
  const isAtt = (c: ColDef<T>) => c.colId === '_att';
  const isSign = (c: ColDef<T>) => c.colId === 'sign_control';

  const badge = cols.find(isBadge);
  const posted = cols.find(isPosted);
  const att = cols.find(isAtt);
  const sign = cols.find(isSign);
  if (!posted && !att && !sign) return cols;

  const rest = cols.filter((c) => !isBadge(c) && !isPosted(c) && !isAtt(c) && !isSign(c));
  const out: ColDef<T>[] = [];
  if (badge) out.push(badge);
  if (posted) {
    out.push({
      ...posted,
      headerName: '', headerClass: 'hdr-icon hdr-posted', headerTooltip: 'Проведено',
      width: ICON_W, minWidth: ICON_W, maxWidth: 32, resizable: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }, cellClass: 'cell-icon',
      valueFormatter: undefined,
      // ч.1218: перенесений документ, не проведений за задумом, — сіра відмітка з поясненням.
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data ? <PostedIcon posted={!!p.data.posted} note={unpostedNoteOf(p.data)} /> : '',
    });
  }
  if (att) {
    out.push({
      ...att,
      headerName: '', headerClass: 'hdr-icon hdr-att', headerTooltip: 'Вкладення',
      width: ICON_W, minWidth: ICON_W, maxWidth: 32, resizable: false, sortable: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }, cellClass: 'cell-icon',
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data ? <AttachmentIcon has={!!p.data._att} /> : '',
    });
  }
  if (sign) {
    out.push({
      ...sign,
      headerName: '', headerClass: 'hdr-icon hdr-sign', headerTooltip: 'Підпис',
      width: ICON_W, minWidth: ICON_W, maxWidth: 32, resizable: false, sortable: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }, cellClass: 'cell-icon',
      cellRenderer: (p: { data?: { __sign_status?: SignStatus } }) =>
        p.data ? <SignIcon status={p.data.__sign_status} /> : '',
    });
  }
  return [...out, ...rest];
}

// --------------------------------------------------------------- DataTable
export function arrangeDtCols<T>(cols: ColumnDef<T>[]): ColumnDef<T>[] {
  const isPosted = (c: ColumnDef<T>) => c.key === 'posted';
  const isAtt = (c: ColumnDef<T>) => c.key === '_att';
  const isSign = (c: ColumnDef<T>) => c.key === 'sign_control';
  const posted = cols.find(isPosted);
  const att = cols.find(isAtt);
  const sign = cols.find(isSign);
  if (!posted && !att && !sign) return cols;

  const status: ColumnDef<T>[] = [];
  if (posted) {
    status.push({
      ...posted, header: <PostedHeaderIcon /> as ReactNode, width: ICON_W, align: 'center',
      cell: (r: T) => <PostedIcon posted={!!(r as { posted?: boolean }).posted} note={unpostedNoteOf(r)} />,
    });
  }
  if (att) status.push({ ...att, header: <AttachmentHeaderIcon /> as ReactNode, width: ICON_W, align: 'center' });
  if (sign) status.push({ ...sign, header: <SignHeaderIcon /> as ReactNode, width: ICON_W, align: 'center' });

  const rest = cols.filter((c) => !isPosted(c) && !isAtt(c) && !isSign(c));
  const badgeIdx = rest.findIndex((c) => c.importBadge);
  if (badgeIdx < 0) return [...status, ...rest];
  return [...rest.slice(0, badgeIdx + 1), ...status, ...rest.slice(badgeIdx + 1)];
}
